import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { Action } from "~/modules/actions/Action";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { getLogger } from "~/modules/logger/LoggerFactory/getLogger";
import { LoggerName } from "~/modules/logger/Logger/LoggerName";
import { ParticipantEventData } from "~/modules/cbm";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { checkReservationExists, getReservationsByTask } from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent } from "~/modules/telemetry/utils";
import { Task } from "~/packages/taskrouter";
import {
    participantDataToTaskParticipant,
    TaskParticipant,
    ExtendedParticipantEventData
} from "~/modules/participant/TaskParticipant";
import { EVENTS } from "~/modules/telemetry/Analytics";

declare module "twilio-taskrouter" {
    interface TaskEvents {
        participantAdded: (task: Task, event: ParticipantEventData) => void;
        participantAddFailed: (task: Task, event: ExtendedParticipantEventData) => void;
        participantRemoved: (task: Task, event: ParticipantEventData) => void;
        participantRemoveFailed: (task: Task, event: ExtendedParticipantEventData) => void;
        participantModified: (task: Task, event: ParticipantEventData) => void;
        participantModifyFailed: (task: Task, event: ExtendedParticipantEventData) => void;
    }
}




export interface TaskParticipantEvent {
    participantAdded: (task: Task, participant: TaskParticipant) => void;
    participantRemoved: (task: Task, participant: TaskParticipant) => void;
    participantModified: (task: Task, participant: TaskParticipant) => void;
    participantAddFailed: (task: Task, participant: TaskParticipant) => void;
    participantRemoveFailed: (task: Task, participant: TaskParticipant) => void;
    participantModifyFailed: (task: Task, participant: TaskParticipant) => void;
}




export interface AddTaskParticipantListenerResponse {
    


    unsubscribe: () => void;
}





































export class AddTaskParticipantListener<T extends keyof TaskParticipantEvent>
    implements Action<Promise<AddTaskParticipantListenerResponse>>
{
    readonly #taskSid: string;

    readonly #eventName: keyof TaskParticipantEvent;

    readonly #listener: TaskParticipantEvent[T];

    constructor(taskSid: string, eventName: T, listener: TaskParticipantEvent[T]) {
        this.#taskSid = taskSid;
        this.#eventName = eventName;
        this.#listener = listener;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Client);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();

        const worker = taskRouter.worker;

        const reservation = getReservationsByTask(worker, this.#taskSid)[0];

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "AddTaskParticipantListener",
            moduleName: "actions/Task",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.AddTaskParticipantListenerCompleted,
            extra: {
                taskSid: this.#taskSid
            }
        });

        const participantListener = (task: Task, participantEventData: ExtendedParticipantEventData) => {
            const taskParticipant = participantDataToTaskParticipant(participantEventData);
            logger.debug(`${this.#eventName} invoked for task ${task.sid}`, taskParticipant);
            this.#listener(task, taskParticipant);
        };
        reservation.task.on(this.#eventName, participantListener);
        sendWorkerTrackingEvent(EVENTS.AddTaskParticipantListenerCompleted, analytics, worker, {
            taskSid: this.#taskSid,
            eventName: this.#eventName,
            reservationSid: reservation.sid
        });
        return {
            unsubscribe: () => reservation.task.off(this.#eventName, participantListener)
        };
    }
}
